import { useCallback, useEffect, useMemo, useRef } from 'react';

import { WizardStep } from '../constant';
import type { IUseModelAgentCreateWizardResult } from '../types';
import {
  createAgentCreateCacheManager,
  hasAgentCreateCacheableProgress,
  type IAgentCreateWizardSnapshot,
} from '../utils/agentCreateCacheManager';

interface IUseModelAgentCreateCacheParams {
  gameCode: string;
  userId: string;
  /** 编辑路由不走本地草稿缓存 */
  isEdit: boolean;
  wizard: IUseModelAgentCreateWizardResult;
}

interface IUseModelAgentCreateCacheResult {
  /** 离开页面前是否提示（未保存草稿且有可恢复进度） */
  shouldPromptOnLeave: boolean;
  /** 写入 localStorage，下次进入可恢复 */
  persistWizardCache: () => void;
  /** 放弃本地进度并清缓存（与 persist 互斥，避免卸载时再次写入） */
  clearWizardCache: () => void;
}

/**
 * 新建向导：在「未 save 草稿」前将 Step1~3 进度写入 localStorage，
 * 按 gameCode + userId 隔离；保存草稿成功或编辑态不读写缓存。
 */
export function useModelAgentCreateCache(
  params: IUseModelAgentCreateCacheParams,
): IUseModelAgentCreateCacheResult {
  const { gameCode, userId, isEdit, wizard } = params;
  const {
    current,
    selectTables,
    fieldConfig,
    yamlDraft,
    getWizardSnapshot,
    restoreWizardSnapshot,
  } = wizard;

  const cacheManagerRef = useRef(createAgentCreateCacheManager());
  /** 仅恢复一次，且须等 connect 注入的 userId 非空（避免首屏空 userId 误判） */
  const cacheRestoredRef = useRef(false);
  /**
   * 离开页时事件回调可能读到过期闭包；用 ref 持有最新快照供 persist / Prompt 判断。
   */
  const snapshotRef = useRef<IAgentCreateWizardSnapshot | null>(null);
  /**
   * persist 挂在 effect cleanup 上；save 草稿后 cacheEnabled 变 false，
   * 须用 ref 避免旧 cleanup 用闭包里的 true 再次写入缓存。
   */
  const cacheEnabledRef = useRef(false);
  /** 用户选择「离开」不保留进度时，跳过卸载 cleanup 的自动 persist */
  const skipPersistOnUnmountRef = useRef(false);

  const cacheEnabled = !isEdit && !yamlDraft.savedConfigUuid;
  cacheEnabledRef.current = cacheEnabled;

  const updateSnapshotRef = useCallback((): void => {
    snapshotRef.current = getWizardSnapshot();
  }, [getWizardSnapshot]);

  useEffect(() => {
    updateSnapshotRef();
  }, [
    current,
    selectTables,
    fieldConfig,
    yamlDraft,
    updateSnapshotRef,
  ]);

  const shouldPromptOnLeave = useMemo(() => {
    if (!cacheEnabled) {
      return false;
    }
    // Step4 已 save 草稿，进度在服务端，不再拦截离开
    if (current === WizardStep.Four) {
      return false;
    }
    // 须用当前 render 快照；snapshotRef 在 useEffect 中更新，会落后 selectTables 一拍
    return hasAgentCreateCacheableProgress(getWizardSnapshot());
  }, [
    cacheEnabled,
    current,
    fieldConfig,
    getWizardSnapshot,
    selectTables,
    yamlDraft,
  ]);

  const clearWizardCache = useCallback((): void => {
    skipPersistOnUnmountRef.current = true;
    cacheManagerRef.current.clear();
  }, []);

  /** 落盘前再次校验进度，空向导不写 localStorage */
  const persistWizardCache = useCallback((): void => {
    if (
      skipPersistOnUnmountRef.current ||
      !cacheEnabledRef.current ||
      !gameCode ||
      !userId
    ) {
      return;
    }
    const payload = snapshotRef.current ?? getWizardSnapshot();
    if (!hasAgentCreateCacheableProgress(payload)) {
      return;
    }
    cacheManagerRef.current.save({
      gameCode,
      userId,
      payload,
    });
  }, [gameCode, getWizardSnapshot, userId]);

  // 新建进入：userId 就绪后按 gameCode + userId 恢复上次未保存草稿的进度
  useEffect(() => {
    if (isEdit || cacheRestoredRef.current || !gameCode || !userId) {
      return;
    }
    cacheRestoredRef.current = true;
    const data = cacheManagerRef.current.load();
    if (
      data?.gameCode === gameCode &&
      data?.userId === userId &&
      data.payload
    ) {
      restoreWizardSnapshot(data.payload);
      snapshotRef.current = data.payload;
    }
  }, [gameCode, isEdit, restoreWizardSnapshot, userId]);

  // 组件卸载时落盘，避免仅依赖 beforeunload 漏存
  useEffect(() => {
    return () => {
      persistWizardCache();
    };
  }, [persistWizardCache]);

  return {
    shouldPromptOnLeave,
    persistWizardCache,
    clearWizardCache,
  };
}
